import CelebrationIcon from '@mui/icons-material/Celebration';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AvailableGiftDTO,
  CouponGiftDTOGiftTypeEnum,
  CouponsV2AvailableGiftDTO,
  CouponsV2GiftDTOGiftTypeEnum,
} from 'probonio-shared-ui/api';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NavigationButton from '../../component/button/NavigationButton';
import { GiftButton } from '../coupons/gift/GiftButton';
import { GiftMetaLines } from '../coupons/gift/GiftMetaLines';
import { GiftV2Button } from '../couponsV2/gift/GiftV2Button';
import { PresentIcon } from '../couponsV2/gift/PresentIcon';

const STACK_DEPTH_MAX = 3;

export type GiftStackItem =
  | { kind: 'v1'; key: string; gift: AvailableGiftDTO }
  | { kind: 'v2'; key: string; gift: CouponsV2AvailableGiftDTO };

/** Matches GiftNavButton / IncentiveNavButton; `div` so it can sit inside the stack tap target. */
function GiftStackPreviewCard({ item }: { item: GiftStackItem }) {
  const { t } = useTranslation('benefitModule');
  const { t: tV2 } = useTranslation('couponsV2Module');

  const isIncentive =
    item.kind === 'v1'
      ? item.gift.giftType === CouponGiftDTOGiftTypeEnum.Incentive
      : item.gift.giftType === CouponsV2GiftDTOGiftTypeEnum.Incentive;

  const isV2Incentive = item.kind === 'v2' && isIncentive;

  const title = isV2Incentive
    ? tV2('incentives.buttonTitle')
    : item.kind === 'v2'
      ? t('benefit.GIFTS.buttonTitle')
      : isIncentive
        ? t('benefit.INCENTIVES.buttonTitle')
        : t('benefit.GIFTS.buttonTitle');

  const subtitle = isV2Incentive
    ? tV2('incentives.buttonSubtitle')
    : item.kind === 'v2'
      ? t('benefit.GIFTS.buttonSubtitle')
      : isIncentive
        ? t('benefit.INCENTIVES.buttonSubtitle')
        : t('benefit.GIFTS.buttonSubtitle');

  const iconLeft = isV2Incentive ? <PresentIcon fontSize="large" /> : <CelebrationIcon fontSize="large" />;

  return (
    <Box sx={{ color: 'primary' }}>
      <NavigationButton
        component="div"
        role="presentation"
        iconLeft={iconLeft}
        iconRight={null}
        sx={{ pointerEvents: 'none', width: '100%' }}
      >
        <Typography variant="h3" color="text.primary">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
        <GiftMetaLines gift={item.gift} />
      </NavigationButton>
    </Box>
  );
}

export const GiftStack: React.FC<{ items: GiftStackItem[] }> = ({ items }) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { t } = useTranslation('benefitModule');
  const [expanded, setExpanded] = useState(false);

  const toggleCollapse = useCallback(() => setExpanded(prev => !prev), []);

  const stackSlice = useMemo(() => items.slice(0, Math.min(STACK_DEPTH_MAX, items.length)), [items]);
  const depth = stackSlice.length;
  const extraCount = items.length > STACK_DEPTH_MAX ? items.length - STACK_DEPTH_MAX : 0;

  const collapseTimeout = prefersReducedMotion ? 0 : undefined;

  if (items.length === 1) {
    return <>{items[0].kind === 'v2' ? <GiftV2Button gift={items[0].gift} /> : <GiftButton gift={items[0].gift} />}</>;
  }

  const shadowForLayer = (pFromFront: number) => theme.shadows[Math.min(2 + pFromFront, 24)];
  const stackOverlap = theme.spacing(3);

  return (
    <Box data-test-id="gift-stack" sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}
      >
        <Typography variant="h3" color="text.primary" sx={{ my: 0, lineHeight: 1.2 }}>
          {t('giftsStack.header', { count: items.length })}
        </Typography>
        <IconButton
          data-test-id="gift-stack-toggle"
          onClick={toggleCollapse}
          size="small"
          color="primary"
          sx={{ flexShrink: 0 }}
          aria-expanded={expanded}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      {!expanded && (
        <Box data-test-id="gift-stack-collapsed-hit-area" display="flex" flexDirection="column" gap={0} onClick={toggleCollapse}>
          <Button
            focusRipple
            aria-expanded={expanded}
            sx={{
              display: 'block',
              width: '100%',
              borderRadius: 1,
              textAlign: 'left',
              padding: 0,
              mt: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {stackSlice.map((item, indexFromBack) => {
                const pFromFront = depth - 1 - indexFromBack;
                const blurRear = !prefersReducedMotion && pFromFront > 0 ? `blur(${Math.min(0.5 + pFromFront * 0.75, 2.25)}px)` : undefined;

                return (
                  <Box
                    key={item.key}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      zIndex: indexFromBack + 1,
                      mt: indexFromBack === 0 ? 0 : `calc(-1 * ${stackOverlap})`,
                      boxShadow: shadowForLayer(pFromFront),
                      borderRadius: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                        ...(blurRear ? { filter: blurRear, opacity: 0.96 } : {}),
                      }}
                    >
                      <GiftStackPreviewCard item={item} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Button>
          {extraCount > 0 && (
            <Typography
              data-test-id="gift-stack-more-count"
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', width: '100%', textAlign: 'center', mt: 1, px: 1, pointerEvents: 'none' }}
            >
              {t('giftsStack.moreCount', { count: extraCount })}
            </Typography>
          )}
        </Box>
      )}

      <Collapse in={expanded} timeout={collapseTimeout} unmountOnExit>
        <Box data-test-id="gift-stack-expanded-list" marginTop={1}>
          {items.map(item => (
            <Box key={item.key}>{item.kind === 'v2' ? <GiftV2Button gift={item.gift} /> : <GiftButton gift={item.gift} />}</Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
